import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Sparkles, ArrowRight, Heart, GraduationCap, Loader2, Presentation, HelpCircle } from "lucide-react";
import { listFeaturedGuides } from "@/lib/api";
import { FounderBadge } from "@/components/FounderBadge";
import { SEO } from "@/components/SEO";

const DEPTH_LABEL = {
  quick: "Quick",
  standard: "Standard",
  deep: "Deep",
  certification: "Certification",
};

const DEPTH_TINT = {
  quick: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  standard: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  deep: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  certification: "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200",
};

const GuideCard = ({ g, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
    data-testid={`featured-card-${i}`}
    className="group flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm hover:shadow-md hover:border-primary/40 transition-all"
  >
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] rounded-full px-2 py-0.5 ${DEPTH_TINT[g.depth] || DEPTH_TINT.standard}`}>
        {DEPTH_LABEL[g.depth] || g.depth}
      </span>
      {g.like_count > 0 && (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
          <Heart className="h-3 w-3 text-rose-500 fill-rose-500" /> {g.like_count}
        </span>
      )}
    </div>

    <h3 className="mt-3 font-serif text-xl leading-tight font-bold line-clamp-2">{g.title}</h3>

    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-accent font-semibold line-clamp-1">
      {g.book_title}{g.chapters ? ` · ${g.chapters}` : ""}
    </p>

    <div className="mt-3 rounded-xl bg-secondary/40 border-l-2 border-accent px-3 py-2 flex-1" data-testid={`featured-teaser-${i}`}>
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-accent mb-1">
        <Sparkles className="h-2.5 w-2.5" /> AI teaser
      </div>
      <p className="text-sm text-foreground/75 leading-relaxed line-clamp-4">
        {(() => {
          const t = g.summary || "A study guide shared by a Lectura learner. Open to see slides, key concepts and citations.";
          const ws = t.trim().split(/\s+/);
          return ws.length <= 60 ? t.trim() : ws.slice(0, 60).join(" ").replace(/[,;:.!?—-]+$/, "") + "…";
        })()}
      </p>
    </div>

    <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1"><Presentation className="h-3.5 w-3.5" /> {g.slide_count} slides</span>
      <span className="inline-flex items-center gap-1"><HelpCircle className="h-3.5 w-3.5" /> {g.quiz_count} quiz</span>
    </div>

    <div className="mt-4 flex items-center justify-between gap-3 pt-4 border-t border-border/60">
      <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
        <span>by <span className="font-medium text-foreground/90">{g.author_name}</span></span>
        {g.author_is_founder && <FounderBadge />}
      </p>
      <Link
        to={g.share_path}
        data-testid={`featured-open-${i}`}
        className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-3 py-1.5 text-xs font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        Open <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  </motion.div>
);

export const FeaturedGuides = () => {
  const [guides, setGuides] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    listFeaturedGuides(24)
      .then((r) => setGuides(r.guides || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load featured guides. Please try again."));
  }, []);

  return (
    <div className="grain min-h-screen">
      <SEO
        title="Featured AI Study Guides — Free Examples | Lectura"
        description="Browse real Lectura study guides made by students. Free examples of AI-narrated lectures, printable guides, and quizzes. No signup required."
        canonical="https://getlectura.app/featured"
      />
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="featured-home-link">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="font-serif text-2xl font-bold tracking-tight leading-none">
              Lectura<span className="text-accent">.</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="featured-signup-cta"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-semibold hover:opacity-90 transition"
          >
            <Sparkles className="h-4 w-4" /> Create your own
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]" data-testid="featured-eyebrow">
            <GraduationCap className="h-3.5 w-3.5" /> Featured study guides
          </span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]" data-testid="featured-hero-heading">
            Great guides,
            <span className="text-accent"> hand-picked</span> from
            <br className="hidden sm:block" /> the Lectura community.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
            Read the slides, skim the key concepts, sample a couple of quiz questions — no signup required.
            When you're ready to keep your own library and hear it read aloud, we'll be here.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {err && (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/10 px-5 py-4 text-sm text-destructive" data-testid="featured-error">
            {err}
          </div>
        )}
        {!guides && !err && (
          <div className="grid place-items-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        )}
        {guides && guides.length === 0 && !err && (
          <div className="text-center py-24" data-testid="featured-empty">
            <p className="font-serif text-2xl font-semibold">No public guides yet</p>
            <p className="mt-2 text-muted-foreground">Be the first to share yours — every guide starts a chain reaction.</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 transition"
            >
              <Sparkles className="h-4 w-4" /> Create the first guide
            </Link>
          </div>
        )}
        {guides && guides.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="featured-grid">
            {guides.map((g, i) => (
              <GuideCard key={g.id || i} g={g} i={i} />
            ))}
          </div>
        )}

        <div className="mt-14 rounded-3xl border border-accent/30 bg-gradient-to-br from-accent/10 via-background to-primary/5 p-8 sm:p-10 text-center" data-testid="featured-bottom-cta">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold">Ready to turn your next chapter into a lecture?</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Paste your reading or just name the book — Lectura writes the lecture, the slides, the study guide and a graded quiz in minutes.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-3 text-sm font-semibold hover:opacity-90 transition"
          >
            <Sparkles className="h-4 w-4" /> Start free
          </Link>
        </div>
      </main>
    </div>
  );
};
